import type { Socket as ServerSocket } from 'socket.io';
import type { Socket as ClientSocket } from 'socket.io-client';
import { EventsMap } from 'socket.io/dist/typed-events';

export type {
  ClientSocket,
  ServerSocket,
};

export type SuggestType = {
  options?: OptionType[],
  default_id?: string,
}

export type TokenClaimsType = {
  sid: string;
  primarySid?: string;
  role: RoleEnum.orderer | RoleEnum.vendor | RoleEnum.supplier;
  groupsid: string;
  organisation?: string;
}

export type BaseSocketData = {
  user_id: string;
  sid: string;
  role: RoleEnum;
}

export type InternalJobEmit<InternalJobs extends EventsMap> = <L extends keyof InternalJobs>(
  event: L,
  ...args: Parameters<InternalJobs[L]>
) => void;

export type ServerEventHandlersTypes<ClientToServerEvents extends EventsMap, ServerToClientEvents extends EventsMap, InternalJobs extends EventsMap> = {
  [K in keyof ClientToServerEvents]: {
    handler: (
      socket: ServerSocket<ClientToServerEvents, ServerToClientEvents>,
      internalJobs: InternalJobEmit<InternalJobs>,
      ...args: Parameters<ClientToServerEvents[K]>) => void;
  }
};

export type ClientEventHandlersTypes<ServerToClientEvents extends EventsMap, ClientToServerEvents extends EventsMap> = {
  [K in keyof ServerToClientEvents]: {
    handler: (socket: ClientSocket<ServerToClientEvents, ClientToServerEvents>, ...args: Parameters<ServerToClientEvents[K]>) => void;
  }
}

export type InternalServerEventHandlersTypes<InternalEvents extends EventsMap> = {
  [K in keyof InternalEvents]: {
    handler: (...args: Parameters<InternalEvents[K]>) => void;
  }
};

export type InternalJobHandlersTypes<InternalJob extends EventsMap> = {
  [K in keyof InternalJob]: (...args: Parameters<InternalJob[K]>) => void;
};

export type GenericSocketType<CTS extends EventsMap, STC extends EventsMap, IS extends EventsMap, SDATA> = ServerSocket<CTS, STC, IS, SDATA>;
export type { EventsMap };

export enum RoleEnum {
  orderer = 'orderer',
  vendor = 'vendor',
  supplier = 'supplier',
}

export type UserCacheType = {
  exp: number;
  user_id: string;
}

export type UserListCachedType = {
  [RoleEnum.orderer]: Record<string, UserCacheType>;
  [RoleEnum.vendor]: Record<string, UserCacheType>;
  [RoleEnum.supplier]: Record<string, UserCacheType>;
};

export type RealtimeOptionsType = {
  corsOrigin?: string;
  corsMethods?: string[];
  getSigningKey: (kid: string) => Promise<import('crypto').KeyObject | null>;
  publicKey?: string;
  accountServiceAddress: string;
  path?: string;
  port?: number;
}

export type ClientRealtimeOptionsType = {
  reconnection: boolean;
  reconnectionAttempts?: number;
  reconnectionDelay?: number;
  timeout: number;
  autoConnect: boolean;
}

export interface AuthError extends Error {
  data: {
    code: string;
  }
}

export type StateCallbackType = 'not_initialized' | 'initializing' | 'auth_failed' | 'server_error' | 'connection_failed' | 'reconnecting' | 'connected' | 'disconnected';

export type BaseRealtimeClientType = {
  serverUrl: string;
  token: string;
  options?: ClientRealtimeOptionsType;
  // eventHandlers?: ClientEventHandlerType[];
  stateCallback?: (state: StateCallbackType, message?: string) => void;
}

export type FieldType = {
  field_value: string,
}


export type ToMainDep = {
  default_strategy: {
    removable: boolean,
    prio: number,
  },
};

export type ToField = {
  default_field_value: string;
};

export type GlobalFieldRulesFieldsType = {
  from: {
    global_field_id: string;
    main?: string;
    dependents?: string[];
  },
  to: ToMainDep | ToField,
};

export type ClientDataValueType = AutoCompleteComponentType | DependentComponentType | FieldComponentType;

// STORED DATA TYPES
export type StoredFieldDefaultValueType = {
  field_value: string | boolean;
  default_field_value?: string;
}

export type RemoveAutoCompleteParaType = {
  data: StoredGlobalFieldsType,
  inputs: {
    global_field_id: string,
    value_ids: string[],
  }[]
};


export type StoredAutoCompleteOptionType = {
  data: {
    [key: string]: string;
  },
  removable: boolean;
  rule_id: string;
  priority: number;
}

export type StoredAutoCompleteFieldType = {
  default_id: string;
  options: StoredAutoCompleteOptionType[];
  process?: {
    parent_field_name: string;
    dependent_field_names?: string[];
  }
}

export type ItemStoredGlobalFieldDataType = {
  type: 'auto_complete' | 'field';
  data: StoredAutoCompleteFieldType | StoredFieldDefaultValueType;
  priority?: number;
  only_override_by_priority?: boolean;
}

export type StoredGlobalFieldsType = {
  global_field_ids: {
    [key: string]: ItemStoredGlobalFieldDataType
  }
};

// RULES TYPES

export type StoredRuleFieldTargetType = {
  merge_strategy: {
    priority: number;
  },
  value_strategy: {
    update_default?: boolean;
    is_unique?: boolean;
    removable: boolean;
    only_override_by_priority?: boolean;
  }
}

export type AutoCompleteSourceType = {
  parent_field_name: string;
  dependent_field_names?: string[];
  format_rules?: {
    [key: string]: string[];
  }
}

export type FieldSourceType = {
  fallback_default_value: string | boolean;
}

export type StoredRuleFieldDataType = {
  type: 'auto_complete' | 'field';
  rule: {
    source: AutoCompleteSourceType | FieldSourceType;
    target: StoredRuleFieldTargetType;
  }
}

export type ItemStoredRuleType = {
  table: string;
  global_fields_ids: {
    [key: string]: StoredRuleFieldDataType
  }
}

// export type StoredRulesType = {
//   rules_ids: {
//     [key: string]: ItemStoredRuleType
//   }
// }
export type StoredRulesType = { [key: string]: ItemStoredRuleType };

// MapRulesToDatabase
export type ParamMapRulesToDatabase = {
  input: any;
  currentGlobalFields: {
    global_field_ids: StoredGlobalFieldsType['global_field_ids'];
  }
  rules: StoredRulesType;
};

export type ResultMapRulesToDatabase = {
  global_field_ids: StoredGlobalFieldsType['global_field_ids']
};

// MapRulesToClient
export type OptionType = { label: string, id: string, removable?: boolean, toolTip?: string, subLabel?: string };
export type ExtendedOptionType = OptionType & { removable: boolean };
export type ComponentTypes = 'auto_complete' | 'dependent' | 'field';

export type AutoCompleteComponentType = {
  component_type: 'auto_complete';
  component_props: {
    default_value: OptionType;
    options: ExtendedOptionType[];
    dependent_field_ids: string[];
  };
}

export type DependentComponentType = {
  component_type: 'dependent';
  component_props: {
    parent_field_name: string;
    map_by: Record<string, string>;
  }
}

export type FieldComponentType = {
  component_type: 'field';
  component_props: {
    default_value: string | boolean;
  }
}

export type ClientComponentType = AutoCompleteComponentType | DependentComponentType | FieldComponentType;

export type ParamMapRulesToClient = {
  global_field_ids: StoredGlobalFieldsType['global_field_ids']
};

export type ResultMapRulesToClientType = {
  global_field_ids: {
    [key: string]: ClientComponentType
  }
}

export type ResultRemoveAutoCompleteType = StoredGlobalFieldsType;
